import { 
  FiGrid, 
  FiList, 
  FiMaximize2,
  FiMinimize2,
  FiLayout,
  FiPlus,
  FiSearch,
  FiFilter,
  FiPlay,
  FiPause,
  FiSettings,
} from "react-icons/fi";
import { useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { useCameraContext } from "../../../context/CameraContext";
import LayoutPresetSelector from "./LayoutPresetSelector";

/**
 * CameraToolbarPro
 * ========================================
 * Enterprise toolbar with:
 * - Search
 * - Status filter
 * - Layout presets
 * - Tour mode
 * - Fullscreen
 * - Add camera
 */
export default function CameraToolbarPro({
  canManageCameras,
  onAddCamera,
  layout,
  setLayout,
  fullscreen,
  toggleFullscreen,
  tourActive,
  setTourActive,
}) {
  const { theme } = useTheme();
  const {
    search,
    setSearch,
    filterStatus,
    setFilterStatus,
    filteredCameras,
  } = useCameraContext();

  const [showLayoutPicker, setShowLayoutPicker] = useState(false);

  const buttonBase = theme === "dark"
    ? "bg-neutral-750 hover:bg-neutral-700 border-neutral-600 text-gray-200"
    : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700";

  const activeButton = theme === "dark"
    ? "bg-blue-600 hover:bg-blue-700 border-blue-600 text-white"
    : "bg-blue-600 hover:bg-blue-700 border-blue-600 text-white";


 return (
  <div className={`flex flex-wrap items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border ${
    theme === "dark" 
      ? "bg-neutral-750 border-neutral-700" 
      : "bg-white border-gray-200"
  }`}>
    {/* Search */}
    <div className="relative flex-1 min-w-[120px] sm:min-w-[200px]">
      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search..."
        className={`w-full pl-10 pr-4 py-2.5 rounded-lg border transition-colors text-sm ${
          theme === "dark"
            ? "bg-neutral-800 border-neutral-600 text-gray-200 placeholder-gray-500"
            : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400"
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
    </div>

    {/* Status Filter */}
    <div className="relative">
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className={`pl-3 pr-8 py-2.5 rounded-lg border transition-colors appearance-none cursor-pointer text-sm ${buttonBase}`}
      >
        <option value="">All</option>
        <option value="online">Online</option>
        <option value="offline">Offline</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>

    {/* Layout Preset Selector */}
    <div className="relative">
      <button
        onClick={() => setShowLayoutPicker(!showLayoutPicker)}
        className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg border transition-all ${buttonBase}`}
        title="Change layout"
      >
        <FiLayout size={18} />
        <span className="text-sm font-medium hidden sm:inline">Layout</span>
      </button>

      {showLayoutPicker && (
        <LayoutPresetSelector
          currentLayout={layout}
          onSelectLayout={(newLayout) => {
            setLayout(newLayout);
            setShowLayoutPicker(false);
          }}
          onClose={() => setShowLayoutPicker(false)}
        />
      )}
    </div>

    {/* Tour Mode */}
    <button
      onClick={() => setTourActive(!tourActive)}
      className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg border transition-all ${
        tourActive ? activeButton : buttonBase
      }`}
      title={tourActive 
        ? "Stop auto-rotation" 
        : "Auto-rotate through cameras"
      }
    >
      {tourActive ? <FiPause size={18} /> : <FiPlay size={18} />}
      <span className="text-sm font-medium hidden sm:inline">Tour</span>
    </button>

    {/* Fullscreen */}
    <button
      onClick={toggleFullscreen}
      className={`p-2.5 rounded-lg border transition-all ${buttonBase}`}
      title={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
    >
      {fullscreen ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
    </button>

    {/* Camera Count - Hide on very small screens */}
    <div className={`hidden md:block px-3 py-2 rounded-lg text-sm font-medium ${
      theme === "dark" ? "text-gray-400" : "text-gray-600"
    }`}>
      {filteredCameras.length} camera{filteredCameras.length !== 1 ? 's' : ''}
    </div>

    {/* Add Camera - Full width on mobile */}
    {canManageCameras && (
      <button
        onClick={onAddCamera}
        className="w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg hover:shadow-xl"
      >
        <FiPlus size={18} />
        <span>Add Camera</span>
      </button>
    )}
  </div>
 );
	
}
